<template>
  <modal
    class="z-top"
    :open="open"
    :click-dismissible="false"
    @close="$emit('close')"
  >
    <template v-slot:default="defaultSlotProps">
      <div class="bg-white fade-in">

        <div
          v-if="loading"
          class="w-full h-72 flex items-center justify-center"
        >
          <span class="sr-only">Loading...</span>
          <svg-loader size="8rem"></svg-loader>
        </div>

        <template v-else>
          <template v-if="showItemListSelect">
            <div class="block border-b text-lg leading-tight pl-6 pr-10 py-4">
              Add selection to a list
            </div>

            <div class="px-6 pt-2">

              <error-alert
                class="flex flex-col gap-y-1 my-4"
                v-if="itemListErrors.length > 0"
                :close-callback="clearItemListErrors"
              >
                <div
                  class="leading-tight"
                  v-for="(error, i) in itemListErrors"
                  v-html="error"
                  :key="i"
                ></div>
              </error-alert>

              <div class="flex flex-col gap-y-3 mt-4">
                <input
                  v-if="itemLists.length > 4"
                  type="text"
                  class="bg-gray-100 border-gray-300 text-sm mb-2"
                  placeholder="Filter lists"
                  @input="updateFilterKeyword($event.target.value)"
                >

                <div class="flex flex-col gap-y-3 max-h-80 overflow-y-auto">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between border border-gray-300 leading-tight px-4 py-3 hover:text-black hover:bg-gray-100 hover:border-gray-600 transition-colors group"
                    v-if="filteredItemLists.length > 0"
                    v-for="itemList in filteredItemLists"
                    :key="itemList.identifier"
                    @click="saveItem({ list: itemList.identifier, sku: selectedVariant.sku })"
                  >
                    <div class="flex">
                      <div class="relative flex items-center justify-start w-10">

                        <div
                          v-if="(itemList.items || []).length > 0"
                          class="relative w-6 h-6"
                          :class="{
                            'w-8 h-8': itemList.items.length === 1,
                            'w-6 h-6': itemList.items.length > 1,
                          }"
                        >
                          <template v-if="itemList.items.length === 2">
                            <div class="h-6 w-6 absolute -top-1 -right-1 transition-all transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                              <img
                                class="object-cover"
                                :src="itemList.items[0].thumbnail"
                                alt=""
                                loading="lazy"
                              >
                            </div>

                            <div class="h-6 w-6 absolute top-1 -left-1 transition-all transform group-hover:-translate-x-0.5 group-hover:translate-y-0.5">
                              <img
                                class="object-cover"
                                :src="itemList.items[1].thumbnail"
                                alt=""
                                loading="lazy"
                              >
                            </div>
                          </template>

                          <template v-else-if="itemList.items.length === 3">
                            <div class="h-6 w-6 absolute top-0 -right-2 transition-all transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                              <img
                                class="object-cover"
                                :src="itemList.items[0].thumbnail"
                                alt=""
                                loading="lazy"
                              >
                            </div>

                            <div class="h-6 w-6 absolute -top-1 -left-1 transition-all transform group-hover:-translate-x-0.5 group-hover:-translate-y-0.5">
                              <img
                                class="object-cover"
                                :src="itemList.items[1].thumbnail"
                                alt=""
                                loading="lazy"
                              >
                            </div>

                            <div class="h-6 w-6 absolute top-1 transition-all transform group-hover:translate-x-0.5 group-hover:translate-y-0.5">
                              <img
                                class="object-cover"
                                :src="itemList.items[2].thumbnail"
                                alt=""
                                loading="lazy"
                              >
                            </div>
                          </template>

                          <div
                            class="h-8 w-8 transition-all transform group-hover:scale-110"
                            v-else
                          >
                            <img
                              class="object-cover"
                              :src="itemList.items[0].thumbnail"
                              alt=""
                              loading="lazy"
                            >
                          </div>
                        </div>
                        <div
                          class="w-8 h-8 border-2 border-dashed border-gray-200 group-hover:border-gray-400 transition-colors"
                          v-else
                        ></div>

                      </div>
                      <div class="flex flex-col text-left">
                        <div class="font-semibold" v-text="itemList.name"></div>
                        <div
                          class="text-xs uppercase tracking-wider"
                          v-text="itemList.private ? 'Private' : 'Shared'"
                        ></div>
                      </div>
                    </div>
                    <div v-text="`${itemList.item_count} items`"></div>
                  </button>
                  <div class="h-12" v-else>
                    <!-- empty -->
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-row flex-wrap items-center justify-between px-6 pb-6 mt-12">
              <button
                type="button"
                class="button button--gray-outline flex items-center justify-center text-xs h-10 p-0 px-4"
                @click="showCreateForm"
              >
                New List
              </button>

              <button
                type="button"
                class="button flex items-center h-10 bg-gray-200 hover:bg-gray-300 text-gray-800 hover:text-gray-900 p-0 px-4"
                @click="$emit('close')"
              >
                Cancel
              </button>
            </div>
          </template><!-- /item list select -->

          <template v-else-if="showItemListCreate">
            <div class="block border-b text-lg leading-tight pl-6 pr-10 py-4">
              Create a new list
            </div>

            <div class="px-6 pt-2 pb-4">

              <error-alert
                class="flex flex-col gap-y-1 my-4"
                v-if="itemListErrors.length > 0"
                :close-callback="clearItemListErrors"
              >
                <div
                  class="leading-tight"
                  v-for="(error, i) in itemListErrors"
                  v-html="error"
                  :key="i"
                ></div>
              </error-alert>

              <div class="label-floating my-4">
                <input
                  type="text"
                  class="w-full bg-gray-100 border-0 border-b border-gray-300 py-4 px-3 text-gray-900 leading-normal focus:border-black focus:outline-none"
                  id="item-list-name"
                  placeholder=" "
                  autocomplete="off"
                  v-model="itemList.name"
                  v-focus
                >
                <label class="absolute block text-gray-500 top-0 left-0 w-full px-3 py-4 leading-normal" for="item-list-name">
                  List Name <span class="text-red-500">*</span>
                </label>
              </div>

              <div class="input-wrap label-floating my-4">
                <textarea
                  class="w-full bg-gray-100 border-0 border-b border-gray-300 py-4 px-3 text-gray-900 leading-normal focus:border-black focus:outline-none"
                  id="description"
                  rows="3"
                  cols="50"
                  placeholder=" "
                  autocomplete="off"
                  v-model="itemList.description"
                ></textarea>
                <label class="absolute block text-gray-500 top-0 left-0 w-full px-3 py-4 leading-normal" for="description">
                  Description (optional)
                </label>
              </div>

              <div class="my-4">
                <label class="inline-flex items-start leading-tight my-4 cursor-pointer">
                  <div class="flex items-center">
                    &#8203;
                    <input
                      type="checkbox"
                      value="1"
                      v-model="itemList.private"
                    >
                  </div>
                  <div class="ml-2">
                    Make this list private
                    <div class="text-sm text-gray-600 mt-1">
                      Lists are visible to all members of your company, unless made private.
                    </div>
                  </div>
                </label>
              </div>

            </div>

            <div class="flex flex-row flex-wrap items-center justify-between px-6 pb-6 mt-4">
              <button
                type="button"
                class="button button--primary-outline flex items-center justify-center w-30 h-12 p-0"
                :class="{'pointer-events-none opacity-50': !itemList.name}"
                @click="createItemList(itemList)"
                :disabled="savingItemList || !itemList.name"
              >
                <span v-if="!savingItemList">Save List</span>
                <svg-loader size="2.5rem" v-else></svg-loader>
              </button>
              <button
                type="button"
                class="button flex items-center h-12 bg-gray-200 hover:bg-gray-300 text-gray-800 hover:text-gray-900 p-0 px-6"
                @click="handleCreateCancel"
                :disabled="savingItemList"
              >
                Cancel
              </button>
            </div>
          </template><!-- /create item list -->

        </template><!-- /not loading -->

      </div>
      <div
        class="absolute inset-0 bg-white-35 w-full h-full"
        v-show="savingToItemList"
      >
        <span class="sr-only">Loading...</span>
        <svg-loader class="absolute top-1/4 left-1/2 -ml-16" size="8rem"></svg-loader>
      </div>
    </template>
  </modal>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue'
import ErrorAlert from '@components/Common/ErrorAlert';
import Modal from '@components/Common/Modal';
import SvgLoader from '@components/Common/SvgLoader';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ItemListModel from '@/models/ItemListModel';

export default {
  name: 'ItemListModal',

  components: {
    ErrorAlert,
    FocusTrap,
    Modal,
    SvgLoader,
  },

  props: {
    open: {
      default: false,
      type: Boolean,
    },
  },

  data: () => ({
    itemList: new ItemListModel,
    loading: true,
  }),

  mounted() {
    const unwatch = this.$watch('open', async value => {
      if(value) {
        unwatch();

        await window.axios.get('/account/item-lists')
          .then(response => {
            if(response.data.length === 0) {
              this.updateItemListCreateDisplay(true);
            } else {
              this.updateItemLists(response.data);
              this.updateItemListCreateDisplay(false);
            }

            this.loading = false;
          })
          .catch(error => console.error(error));
        }
    });
  },

  methods: {
    ...mapMutations('itemLists', [
      'updateFilterKeyword',
      'updateItemListCreateDisplay',
      'updateItemLists',
    ]),

    ...mapActions('itemLists', [
      'clearItemListErrors',
      'createItemList',
      'saveToItemList',
    ]),

    handleCreateCancel() {
      if(this.itemLists.length === 0) {
        this.$emit('close');
      }

      this.updateItemListCreateDisplay(false);
    },

    async saveItem(identifier, sku) {
      await this.saveToItemList(identifier, sku);

      if(this.itemListErrors.length === 0) {
        this.$emit('close');
      }
    },

    showCreateForm() {
      this.clearItemListErrors();
      this.updateItemListCreateDisplay(true);
    },
  },

  computed: {
    ...mapState('product', [
      'selectedVariant',
    ]),

    ...mapState('itemLists', [
      'itemListErrors',
      'itemLists',
      'savingItemList',
      'savingToItemList',
      'showItemListCreate',
    ]),

    ...mapGetters('itemLists', [
      'filteredItemLists',
      'showItemListSelect',
    ]),
  },

  watch: {
    open(value) {
      this.clearItemListErrors();

      if(!value) {
        this.updateItemListCreateDisplay(false);
      }
    },

    showItemListCreate(value) {
      if(!value) {
        this.itemList = new ItemListModel;
      }
    },
  },
}
</script>
